import styles from './responsive-tv-page.scss?raw';
import greenCalendar from '../../../assets/green-calendar-icon.svg';
import { useGetFlights } from './flightCateringManagementQueries';
import { ErrorPopup } from './ErrorPopup';
import React, { useState } from 'react';
import Loader from './Loader';
import { pageBackFlippingDuration, intervalApiCateringCall } from '../../../config';
import { useTranslation } from 'react-i18next';
import { setTimeout } from 'worker-timers';
import RAMlogoImage from '../../../assets/AgentLogoRam.svg';

/* Inner span that carries the 2-line clamp.
   NEVER put display:-webkit-box on a <td> — it kills table-cell layout. */
function C({ children }: { children: React.ReactNode }) {
  return <span className="ci">{children}</span>;
}

const isAlert = (d1: string, d2: string) => {
  const numbderD1 = Number(d1);
  const numbderD2 = Number(d2);
  if (isNaN(numbderD1) || isNaN(numbderD2)) {
    return false;
  }

  if (numbderD1 > numbderD2) {
    return true;
  }
  return false;
};

export default function FlightCateringManagement() {
  const [page, setPage] = useState(0);
  const { t } = useTranslation();
  const params = new URLSearchParams(window.location.search);
  const plusHours = params.get('plusHours') || '';

  const { flightData, isLoading, showErrorPopup, hasPreviousData, dismissPopup, goHome, refetch } =
    useGetFlights();

  const tbodyRef = React.useRef<HTMLTableSectionElement>(null);
  const flipCountRef = React.useRef(0);
  const versionRef = React.useRef(0);

  const runFlip2 = () => {
    setTimeout(() => {
      refetch();
    }, intervalApiCateringCall);
  };

  React.useEffect(() => {
    if (!!flightData?.data && flightData?.data?.length <= 15) {
      runFlip2();
      return;
    }
    const version = ++versionRef.current;

    flipCountRef.current = 0;
    setPage(0);

    const tbody = tbodyRef.current;

    // reset DOM safely
    if (tbody) {
      const trs = [...tbody.querySelectorAll('tr')];

      trs.forEach((tr) => {
        const el = tr as HTMLElement;

        // initial hidden state (for entry animation)
        el.style.transition = 'none';
        el.style.transform = 'rotateX(90deg) translateY(6px)';
        el.style.opacity = '0';
      });

      // force reflow
      tbody.getBoundingClientRect();

      requestAnimationFrame(() => {
        if (version !== versionRef.current) return;

        trs.forEach((tr, i) => {
          const el = tr as HTMLElement;

          el.style.transition = `transform 0.25s ease-out ${i * 40}ms, opacity 0.25s ease-out ${
            i * 40
          }ms`;
          el.style.transform = 'rotateX(0deg)';
          el.style.opacity = '1';
        });
      });
    }

    const runFlip = () => {
      if (version !== versionRef.current) return;

      setTimeout(() => {
        if (version !== versionRef.current) return;

        // 🔴 CHECK FIRST (this is the key fix)
        if (flipCountRef.current >= 3) {
          flipCountRef.current = 0;
          refetch();
          return;
        }

        const tbody = tbodyRef.current;
        if (!tbody) return;

        const trs = [...tbody.querySelectorAll('tr')];

        // ── flip OUT ──
        trs.forEach((tr, i) => {
          const el = tr as HTMLElement;
          el.style.transition = `transform 0.18s ease-in ${i * 40}ms, opacity 0.18s ease-in ${
            i * 40
          }ms`;
          el.style.transform = 'rotateX(90deg) translateY(-4px)';
          el.style.opacity = '0';
        });

        const flipOutDuration = trs.length * 40 + 200;

        setTimeout(() => {
          if (version !== versionRef.current) return;

          flipCountRef.current += 1;

          setPage((p) => (p === 0 ? 1 : 0));

          requestAnimationFrame(() => {
            if (version !== versionRef.current) return;

            const tbody = tbodyRef.current;
            if (!tbody) return;

            const newTrs = [...tbody.querySelectorAll('tr')];

            newTrs.forEach((tr) => {
              const el = tr as HTMLElement;
              el.style.transition = 'none';
              el.style.transform = 'rotateX(90deg) translateY(4px)';
              el.style.opacity = '0';
            });

            tbody.getBoundingClientRect();

            requestAnimationFrame(() => {
              if (version !== versionRef.current) return;

              newTrs.forEach((tr, i) => {
                const el = tr as HTMLElement;
                el.style.transition = `transform 0.22s ease-out ${
                  i * 40
                }ms, opacity 0.22s ease-out ${i * 40}ms`;
                el.style.transform = 'rotateX(0deg)';
                el.style.opacity = '1';
              });

              runFlip();
            });
          });
        }, flipOutDuration);
      }, pageBackFlippingDuration);
    };

    runFlip();

    return () => {
      versionRef.current += 1;
    };
  }, [flightData]);

  if (isLoading) {
    return <Loader />;
  }

  const rows = (flightData?.data ?? []).slice(0, 30);
  const pageRows = rows?.length <= 15 ? rows : rows.slice(page * 15, page * 15 + 15);

  return (
    <div className="fcm-root">
      <style>{styles}</style>
      {/* ── Header ── */}
      <header className="fcm-header">
        <div className="fcm-title">
          <img src={RAMlogoImage} />
          {t('flightCateringManagement')}{' '}
          {!!plusHours && `H-${plusHours}${plusHours === '4' ? ' - H-2' : ' - H-0'}`}
        </div>
        <div className="fcm-datetime">
          <img src={greenCalendar} alt="time" />
          Dernière MAJ: {flightData?.lastUpdate}
        </div>
      </header>

      {/* ── Table ── */}
      <div className="fcm-table-wrap">
        {/* Sticky thead */}
        <div className="fcm-thead-scroll">
          <table className="fcm-table">
            <colgroup>
              <col className="col-at" />
              <col className="col-at" />
              <col className="col-dest" />
              <col className="col-std" />
              <col className="col-etd" />
              <col className="col-ac" />
              <col className="col-reg" />
              <col className="col-pkg" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-eq" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-eq" />
              <col className="col-spml" />
              <col className="col-spml" />
              <col className="col-psv" />
              <col className="col-psv" />
              <col className="col-psv" />
              <col className="col-rem" />
            </colgroup>
            <thead>
              <tr>
                <th rowSpan={3} className="th-rem">
                  AT
                </th>
                <th rowSpan={3} className="th-rem">
                  RT
                </th>
                <th rowSpan={3} className="th-rem">
                  DEST
                </th>
                <th rowSpan={3} className="th-rem">
                  STD
                </th>
                <th rowSpan={3} className="th-rem">
                  ETD
                </th>
                <th rowSpan={3}>AC</th>
                <th rowSpan={3}>REG</th>
                <th rowSpan={3}>PKG</th>
                <th colSpan={5} className="th-group-prev">
                  PREVESIONS
                </th>
                <th colSpan={5} className="th-group-rempl">
                  REMPLISSAGE
                </th>
                <th colSpan={2} className="th-group-spml">
                  SPML
                </th>
                <th colSpan={3} className="th-group-psv">
                  PSV (A/R)
                </th>
                <th rowSpan={3} className="th-rem">
                  REMARQUES
                </th>
              </tr>
              <tr>
                <th colSpan={2} className="th-sub-prev">
                  ALLER
                </th>
                <th colSpan={2} className="th-sub-prev">
                  RETOUR
                </th>
                <th rowSpan={2} className="th-sub-prev">
                  EQ
                </th>
                <th colSpan={2} className="th-sub-rempl">
                  ALLER
                </th>
                <th colSpan={2} className="th-sub-rempl">
                  RETOUR
                </th>
                <th rowSpan={2} className="th-sub-rempl1">
                  EQ
                </th>
                <th rowSpan={2} className="th-rem">
                  A
                </th>
                <th rowSpan={2} className="th-rem">
                  R
                </th>
                <th rowSpan={2} className="th-rem">
                  BCL
                </th>
                <th rowSpan={2} className="th-rem">
                  YCL
                </th>
                <th rowSpan={2} className="th-rem">
                  PEQ
                </th>
              </tr>
              <tr>
                <th className="th-sub-prev">BC</th>
                <th className="th-sub-prev">YC</th>
                <th className="th-sub-prev">BC</th>
                <th className="th-sub-prev">YC</th>
                <th className="th-sub-rempl1">BC</th>
                <th className="th-sub-rempl1">YC</th>
                <th className="th-sub-rempl1">BC</th>
                <th className="th-sub-rempl1">YC</th>
              </tr>
            </thead>
          </table>
        </div>

        {/* Scrollable tbody */}
        <div className="fcm-tbody-scroll">
          <table className="fcm-table">
            <colgroup>
              <col className="col-at" />
              <col className="col-at" />
              <col className="col-dest" />
              <col className="col-std" />
              <col className="col-etd" />
              <col className="col-ac" />
              <col className="col-reg" />
              <col className="col-pkg" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-eq" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-num" />
              <col className="col-eq" />
              <col className="col-spml" />
              <col className="col-spml" />
              <col className="col-psv" />
              <col className="col-psv" />
              <col className="col-psv" />
              <col className="col-rem" />
            </colgroup>
            <tbody ref={tbodyRef}>
              {pageRows.map((f, i) => {
                return (
                  <tr key={i}>
                    <td className="td-at">
                      <C>{f.at}</C>
                    </td>
                    <td className="td-at">
                      <C>{f.return}</C>
                    </td>
                    <td className="td-dest">
                      <C>{f.dest}</C>
                    </td>
                    <td className="td-time">
                      <C>{f.std}</C>
                    </td>
                    <td className="td-time">
                      <C>{f.etd}</C>
                    </td>
                    <td>
                      <C>{f.ac}</C>
                    </td>
                    <td>
                      <C>{f.reg}</C>
                    </td>
                    <td>
                      <C>{f.pkg}</C>
                    </td>
                    <td className="prevTd">
                      <C>{f.prev_ALLER_BC}</C>
                    </td>
                    <td className="prevTd">
                      <C>{f.prev_ALLER_YC}</C>
                    </td>
                    <td className="prevTd">
                      <C>{f.prev_RETOUR_BC}</C>
                    </td>
                    <td className="prevTd">
                      <C>{f.prev_RETOUR_YC}</C>
                    </td>
                    <td className="prevTd">
                      <C>{f.prev_ALLER_EQ}</C>
                    </td>
                    <td
                      className={`rempTd ${
                        isAlert(f.remp_ALLER_BC, f.prev_ALLER_BC) ? 'td-alert' : ''
                      }`}
                    >
                      <C>{f.remp_ALLER_BC}</C>
                    </td>
                    <td
                      className={`rempTd ${
                        isAlert(f.remp_ALLER_YC, f.prev_ALLER_YC) ? 'td-alert' : ''
                      }`}
                    >
                      <C>{f.remp_ALLER_YC}</C>
                    </td>
                    <td
                      className={`rempTd ${
                        isAlert(f.remp_RETOUR_BC, f.prev_RETOUR_BC) ? 'td-alert' : ''
                      }`}
                    >
                      <C>{f.remp_RETOUR_BC}</C>
                    </td>
                    <td
                      className={`rempTd ${
                        isAlert(f.remp_RETOUR_YC, f.prev_RETOUR_YC) ? 'td-alert' : ''
                      }`}
                    >
                      <C>{f.remp_RETOUR_YC}</C>
                    </td>
                    <td
                      className={`rempTd ${
                        isAlert(f.remp_ALLER_EQ, f.prev_ALLER_EQ) ? 'td-alert' : ''
                      }`}
                    >
                      <C>{f.remp_ALLER_EQ}</C>
                    </td>
                    <td>
                      <C>{f.spml_A}</C>
                    </td>
                    <td>
                      <C>{f.spml_R}</C>
                    </td>
                    <td className="td-psv">
                      <C>{f.psv_BCL}</C>
                    </td>
                    <td className="td-psv">
                      <C>{f.psv_YCL}</C>
                    </td>
                    <td className="td-psv">
                      <C>{f.psv_PEQ}</C>
                    </td>
                    <td className={'td-rem'}>{f.remarques}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {showErrorPopup && (
        <ErrorPopup
          hasPreviousData={hasPreviousData}
          onCancel={dismissPopup}
          onBackToHome={goHome}
        />
      )}
    </div>
  );
}
